import React from 'react';
import { Box, Typography, IconButton, Chip, Tooltip, Switch } from '@mui/material';
import { FaTrash, FaExclamationCircle } from 'react-icons/fa';
import LoadingSpinner from '../LoadingSpinner';
import { EmployeeAvatar } from '../EmployeeAvatar';
import EditableCell from './EditableCell';
import StatusCell from './StatusCell';

const UserStatusCell = ({ item, config }) => {
  const statusConfig = config.statusConfig.userStatuses[item.userStatus];
  
  if (!statusConfig) {
    return (
      <Chip
        label="Unknown"
        size="small"
        variant="outlined"
        sx={{
          borderColor: '#6B7280',
          color: '#6B7280'
        }}
      />
    );
  }

  const content = (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      gap: 1,
      height: '100%',
      justifyContent: 'flex-start'
    }}>
      {item.adDetails && (
        <EmployeeAvatar 
          employee={{ 
            azureId: item.adDetails?.adId, 
            fullName: item.adDetails?.fullName 
          }} 
          size={24} 
        />
      )}
      <Chip
        label={statusConfig.label}
        size="small"
        variant="outlined"
        sx={{
          borderColor: statusConfig.color,
          color: statusConfig.color
        }}
      />
    </Box>
  );

  return statusConfig.tooltip ? (
    <Tooltip title={statusConfig.tooltip}>
      {content}
    </Tooltip>
  ) : content;
};

const ProjectStatusCell = ({ item, config }) => {
  const statusConfig = config.statusConfig.projectStatuses[item.projectStatus];
  
  if (!statusConfig) {
    return (
      <Chip
        label="Unknown"
        size="small"
        variant="outlined"
        sx={{
          borderColor: '#6B7280',
          color: '#6B7280'
        }}
      />
    );
  }

  const content = (
    <Chip
      label={statusConfig.label}
      size="small"
      variant="outlined"
      sx={{
        borderColor: statusConfig.color,
        color: statusConfig.color
      }}
    />
  );

  return statusConfig.tooltip ? (
    <Tooltip title={statusConfig.tooltip}>
      {content}
    </Tooltip>
  ) : content;
};

const AssignmentStatusCell = ({ item, config }) => {
  const statusConfig = config.statusConfig.assignmentStatuses[item.assignmentStatus];
  
  if (!statusConfig) {
    return (
      <Chip
        label="Unknown"
        size="small"
        variant="outlined"
        sx={{
          borderColor: '#6B7280',
          color: '#6B7280'
        }}
      />
    );
  }

  const content = (
    <Chip
      label={statusConfig.label}
      size="small"
      variant="outlined"
      sx={{
        borderColor: statusConfig.color,
        color: statusConfig.color
      }}
    />
  );

  return statusConfig.tooltip ? (
    <Tooltip title={statusConfig.tooltip}>
      {content}
    </Tooltip>
  ) : content;
};

const ImportTable = ({ 
  config, 
  data, 
  loading, 
  editingCell, 
  isSelectOpen, 
  onCellEdit, 
  onDeleteItem, 
  onToggleErrorPanel, 
  expandedErrorPanels = [], 
  fieldContext, 
  onSelectOpen, 
  onSelectClose, 
  onStartEdit, 
  onStopEdit,
  // Additional props for customization
  customRowRenderer,
  showDeleteButton = true,
  deleteButtonTitle = "Remove from import",
  emptyStateMessage,
  emptyStateColSpan,
  // Permission checks for editing (optional)
  canEdit,
  canDelete
}) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <LoadingSpinner />
      </Box>
    );
  }

  const dataFields = config.tableColumns?.filter(col => 
    col.field !== 'status' && 
    col.field !== 'userStatus' && 
    col.field !== 'projectStatus' && 
    col.field !== 'assignmentStatus' && 
    col.field !== 'actions'
  ) || Object.keys(config.fields).map(field => ({ field, label: config.fields[field].label }));

  return (
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #E5E7EB' }}>
            <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, fontSize: '14px' }}>#</th>
            <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, fontSize: '14px' }}>Status</th>
            {config.statusConfig?.userStatuses && (
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, fontSize: '14px' }}>User Status</th>
            )}
            {config.statusConfig?.projectStatuses && (
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, fontSize: '14px' }}>Project Status</th>
            )}
            {config.statusConfig?.assignmentStatuses && (
              <th style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, fontSize: '14px' }}>Assignment Status</th>
            )}
            {dataFields.map(col => (
              <th key={col.field} style={{ padding: '12px 8px', textAlign: 'left', fontWeight: 600, fontSize: '14px' }}>
                {col.label}
              </th>
            ))}
            {showDeleteButton && (
              <th style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 600, fontSize: '14px' }}>Actions</th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && emptyStateMessage && (
            <tr>
              <td colSpan={emptyStateColSpan || (3 + 
                (config.statusConfig?.userStatuses ? 1 : 0) + 
                (config.statusConfig?.projectStatuses ? 1 : 0) + 
                (config.statusConfig?.assignmentStatuses ? 1 : 0) +
                dataFields.length + (showDeleteButton ? 1 : 0)
              )} style={{ padding: '24px', textAlign: 'center', color: '#6B7280' }}>
                {emptyStateMessage}
              </td>
            </tr>
          )}
          
          {data.map((item, index) => {
            const isExpanded = expandedErrorPanels.includes(item.id);
            const errors = item.validationErrors || [];
            const hasErrors = (item.validationStatus === 'invalid' || item.importStatus === 'failed') && errors.length > 0;
            const isImportError = item.importStatus === 'failed';
            
            // Allow custom row rendering
            if (customRowRenderer) {
              return customRowRenderer(item, index, {
                dataFields,
                editingCell,
                isSelectOpen,
                onCellEdit,
                onDeleteItem,
                onSelectOpen,
                onSelectClose,
                onStartEdit,
                onStopEdit,
                fieldContext,
                config,
                showDeleteButton,
                deleteButtonTitle
              });
            }
            
            return (
              <React.Fragment key={item.id}>
                <tr style={{
                  borderBottom: '1px solid #E5E7EB',
                  backgroundColor: item.editable === false ? '#F3F4F6' : 'white',
                }}>
                  <td style={{ padding: '8px', fontSize: '14px' }}>{index + 1}</td>
                  <td style={{ padding: '8px' }}>
                    <StatusCell 
                      item={item} 
                      config={config} 
                      onToggleErrorPanel={onToggleErrorPanel} 
                    />
                  </td>
                  {config.statusConfig?.userStatuses && (
                    <td style={{ padding: '8px' }}>
                      <UserStatusCell item={item} config={config} />
                    </td>
                  )}
                  {config.statusConfig?.projectStatuses && (
                    <td style={{ padding: '8px' }}>
                      <ProjectStatusCell item={item} config={config} />
                    </td>
                  )}
                  {config.statusConfig?.assignmentStatuses && (
                    <td style={{ padding: '8px' }}>
                      <AssignmentStatusCell item={item} config={config} />
                    </td>
                  )}
                  {dataFields.map(col => {
                    const fieldConfig = config.fields[col.field];
                    
                    // Handle boolean fields with switches
                    if (fieldConfig?.type === 'boolean') {
                      return (
                        <td key={col.field} style={{ padding: '8px', textAlign: 'center' }}>
                          <Switch
                            checked={item[col.field] || false}
                            onChange={(e) => onCellEdit(item.id, col.field, e.target.checked)}
                            size="small"
                            disabled={canEdit ? !canEdit(item, col.field) : false}
                          />
                        </td>
                      );
                    }
                    
                    // Default handling with EditableCell
                    return (
                      <td key={col.field} style={{ padding: '0' }}>
                        <EditableCell
                          config={config}
                          item={item}
                          field={col.field}
                          value={item[col.field]}
                          onEdit={onCellEdit}
                          isEditing={editingCell?.id === item.id && editingCell?.field === col.field}
                          isSelectOpen={isSelectOpen}
                          onSelectOpen={onSelectOpen}
                          onSelectClose={onSelectClose}
                          onStartEdit={onStartEdit}
                          onStopEdit={onStopEdit}
                          fieldContext={fieldContext}
                        />
                      </td>
                    );
                  })}
                  {showDeleteButton && (
                    <td style={{ padding: '8px', textAlign: 'center' }}>
                      <IconButton
                        size="small"
                        onClick={() => onDeleteItem(item.id)}
                        sx={{ 
                          color: '#EF4444',
                          width: '32px',
                          height: '32px',
                          '&:hover': {
                            backgroundColor: '#FEE2E2'
                          },
                          '& svg': {
                            width: '14px',
                            height: '14px'
                          }
                        }}
                        title={deleteButtonTitle}
                      >
                        <FaTrash />
                      </IconButton>
                    </td>
                  )}
                </tr>
                {isExpanded && hasErrors && (
                  <tr>
                    <td colSpan={3 + 
                      (config.statusConfig?.userStatuses ? 1 : 0) + 
                      (config.statusConfig?.projectStatuses ? 1 : 0) + 
                      (config.statusConfig?.assignmentStatuses ? 1 : 0) +
                      dataFields.length + (showDeleteButton ? 1 : 0)
                    } style={{ padding: 0 }}>
                      <Box 
                        sx={{ 
                          backgroundColor: '#FEF2F2', 
                          borderLeft: '4px solid #EF4444',
                          margin: '0 16px 8px 16px',
                          borderRadius: '4px',
                          animation: 'slideDown 0.3s ease-out',
                          '@keyframes slideDown': {
                            '0%': { opacity: 0, maxHeight: 0 },
                            '100%': { opacity: 1, maxHeight: '200px' }
                          }
                        }}
                      >
                        <Box sx={{ p: 2 }}>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 600, 
                            color: '#DC2626',
                            mb: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <Box component="span" sx={{ fontSize: '14px', display: 'inline-flex', alignItems: 'center' }}>
                              <FaExclamationCircle />
                            </Box>
                            {isImportError ? 'Import Errors' : 'Validation Errors'} ({errors.length})
                          </Typography>
                          <Box component="ul" sx={{ 
                            margin: 0, 
                            paddingLeft: '20px',
                            color: '#7F1D1D'
                          }}>
                            {errors.map((error, errorIndex) => (
                              <li key={errorIndex} style={{ marginBottom: '4px' }}>
                                <Box component="span" sx={{ fontWeight: 500 }}>
                                  {error.field === 'import' ? 'Import Error' : error.field}:
                                </Box>{' '}
                                {error.message}
                              </li>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
};

export default ImportTable;